import {
  Card,
  createStyles,
  Divider,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { AuditLogActions } from '../../../enums/retrieve-audit-logs.enum';
import { formatDateWithTime } from '../../../helpers/date-convert';
import { GetEnumKeyByEnumValue } from '../../../helpers/payment-link.helper';
import { BasicAuditLog } from '../../../type/retrieve-audit-logs.types';
import XSafeTextInfo from '../../x-components/x-safe-text-info.component';
import {
  LogsDetailsSectionApprovedAsRow,
  LogsDetailsSectionApprovedParentEntities,
  LogsDetailsSectionCreateMidsRow,
  LogsDetailsSectionDefaultRow,
  LogsDetailsSectionExportRow,
  LogsDetailsSectionMidsUnassignRow,
  LogsDetailsSectionUpdateRow,
  LogsDetailsSectionCreatePaymentRow,
} from './logs-details-section-table-rows.component';

function LogsDetailsSection(props: Props) {
  const { selectedLog } = props;
  const { classes } = useStyles();

  return (
    <>
      <Card withBorder p="xl" sx={{ width: '100%' }}>
        <Stack>
          <Title order={4}>Details</Title>
          <Divider />
          <div className={classes.tableContainer}>
            <Table className={classes.logsDetails}>
              <tbody>
                <tr>
                  <td style={{ backgroundColor: '#F6F8FB' }}>
                    <Text weight={600} align="right" size="sm">
                      Performed by
                    </Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {selectedLog.details[0].originUsername}
                    </Text>
                  </td>
                </tr>
                <tr>
                  <td style={{ backgroundColor: '#F6F8FB' }}>
                    <Text weight={600} align="right" size="sm">
                      Timestamp
                    </Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {formatDateWithTime(selectedLog.dateCreated.toString())}
                    </Text>
                  </td>
                </tr>
                <tr>
                  <td style={{ backgroundColor: '#F6F8FB' }}>
                    <Text weight={600} align="right" size="sm">
                      Action
                    </Text>
                  </td>
                  <td>
                    <XSafeTextInfo
                      data={GetEnumKeyByEnumValue(
                        AuditLogActions,
                        selectedLog.logActionId,
                      )}
                    />
                  </td>
                </tr>
                <tr>{dynamicDetailsRow(selectedLog)}</tr>
                {selectedLog.logActionId ===
                  AuditLogActions.APPROVE_ONBOARDING && (
                  <>
                    <tr>
                      <LogsDetailsSectionApprovedParentEntities
                        selectedLog={selectedLog}
                      />
                    </tr>
                    <tr>
                      <LogsDetailsSectionApprovedAsRow
                        selectedLog={selectedLog}
                      />
                    </tr>
                  </>
                )}
                {selectedLog.logActionId === AuditLogActions.CREATE_POS && (
                  <>
                    <tr>
                      <td style={{ backgroundColor: '#F6F8FB' }}>
                        <Text weight={600} align="right" size="sm">
                          POS name
                        </Text>
                      </td>
                      <td>
                        <XSafeTextInfo data={selectedLog.details[0].posName} />
                      </td>
                    </tr>
                  </>
                )}
                {selectedLog.logActionId === AuditLogActions.ASSIGN_MID && (
                  <>
                    <tr>
                      <td style={{ backgroundColor: '#F6F8FB' }}>
                        <Text weight={600} align="right" size="sm">
                          MID assigned
                        </Text>
                      </td>
                      <td>
                        <XSafeTextInfo data={selectedLog.details[0].midName} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: '#F6F8FB' }}>
                        <Text weight={600} align="right" size="sm">
                          Target POSs
                        </Text>
                      </td>
                      <td>
                        <Stack>
                          {selectedLog.details.map((detail, index) => {
                            return detail.posNames?.map((posName) => {
                              return <XSafeTextInfo data={posName} />;
                            });
                          })}
                        </Stack>
                      </td>
                    </tr>
                  </>
                )}
                {selectedLog.logActionId === AuditLogActions.UNASSIGN_MID && (
                  <>
                    <tr>
                      <td style={{ backgroundColor: '#F6F8FB' }}>
                        <Text weight={600} align="right" size="sm">
                          Target POS
                        </Text>
                      </td>
                      <td>
                        <XSafeTextInfo data={selectedLog.details[0].posName} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: '#F6F8FB' }}>
                        <Text weight={600} align="right" size="sm">
                          MIDs unassigned
                        </Text>
                      </td>
                      <td>
                        <Stack>
                          {selectedLog.details.map((detail, index) => {
                            return <XSafeTextInfo data={detail.midName} />;
                          })}
                        </Stack>
                      </td>
                    </tr>
                  </>
                )}
                {(selectedLog.logActionId === AuditLogActions.UPDATE_MID ||
                  selectedLog.logActionId === AuditLogActions.DISABLE_MID ||
                  selectedLog.logActionId === AuditLogActions.ENABLE_MID) && (
                  <>
                    <tr>
                      <td style={{ backgroundColor: '#F6F8FB' }}>
                        <Text weight={600} align="right" size="sm">
                          Target MID
                        </Text>
                      </td>
                      <td>
                        <XSafeTextInfo data={selectedLog.details[0].midName} />
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </Stack>
      </Card>
    </>
  );
}

export default LogsDetailsSection;

const dynamicDetailsRow = (selectedLog: BasicAuditLog) => {
  let row = <></>;
  if (
    selectedLog.logActionId === AuditLogActions.UPDATE_ONBOARDING ||
    selectedLog.logActionId === AuditLogActions.UPDATE_POS
  ) {
    row = <LogsDetailsSectionUpdateRow selectedLog={selectedLog} />;
  } else if (selectedLog.logActionId === AuditLogActions.EXPORT_TRANSACTIONS) {
    row = <LogsDetailsSectionExportRow selectedLog={selectedLog} />;
  } else if (
    selectedLog.logActionId === AuditLogActions.UNASSIGN_MID ||
    selectedLog.logActionId === AuditLogActions.ASSIGN_MID
  ) {
    row = <LogsDetailsSectionMidsUnassignRow selectedLog={selectedLog} />;
  } else if (
    selectedLog.logActionId === AuditLogActions.CREATE_MID ||
    selectedLog.logActionId === AuditLogActions.UPDATE_MID ||
    selectedLog.logActionId === AuditLogActions.ENABLE_MID ||
    selectedLog.logActionId === AuditLogActions.DISABLE_MID
  ) {
    row = <LogsDetailsSectionCreateMidsRow selectedLog={selectedLog} />;
  } else if (selectedLog.logActionId === AuditLogActions.CREATE_PAYMENT_LINK) {
    row = <LogsDetailsSectionCreatePaymentRow selectedLog={selectedLog} />;
  } else {
    row = <LogsDetailsSectionDefaultRow selectedLog={selectedLog} />;
  }

  return row;
};

interface Props {
  selectedLog: BasicAuditLog;
}

const useStyles = createStyles(() => {
  return {
    logsDetails: {
      whiteSpace: 'nowrap',
    },
    tableContainer: {
      whiteSpace: 'nowrap',
      overflowX: 'auto',
      overflowY: 'hidden',
    },
  };
});
